import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from 'app/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from 'app/components/ui/select';
import { useUserchatSlice } from '../slice';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters long'),
});

const signUpSchema = loginSchema.extend({
  name: z.string().min(2, 'Name must be at least 2 characters long'),
  username: z.string().min(3, 'Username must be at least 3 characters long'),
  bio: z.string().optional(),
  role: z.enum(['admin', 'user']).default('user'),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type SignUpFormValues = z.infer<typeof signUpSchema>;

const ChatAuthForm: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const { useLoginUserMutation, useSignUpUserMutation } = useUserchatSlice();

  const [loginUser, { isLoading: loginLoading }] = useLoginUserMutation();
  const [signUpUser, { isLoading: signUpLoading }] = useSignUpUserMutation();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LoginFormValues | SignUpFormValues>({
    resolver: zodResolver(isLogin ? loginSchema : signUpSchema),
  });

  const onSubmit = async (data: any) => {
    try {
      if (isLogin) {
        const response = await loginUser({
          email: data.email,
          password: data.password,
        }).unwrap();
        console.log('Login Successful:', response);

        if (response?.token) {
          localStorage.setItem('authToken', response.token);
          console.log('Token saved to localStorage');
        }
      } else {
        const response = await signUpUser({
          name: data.name,
          username: data.username,
          email: data.email,
          password: data.password,
          bio: data.bio || '',
          role: data.role,
        }).unwrap();
        console.log('Sign-Up Successful:', response);

        if (response?.token) {
          localStorage.setItem('authToken', response.token);
          console.log('Token saved to localStorage');
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const selectedRole = watch('role', 'user');

  return (
    <div className="flex justify-center items-center h-screen bg-gray-50">
      <Card className="w-full max-w-md p-4 shadow-md">
        <CardHeader>
          <CardTitle>{isLogin ? 'Login' : 'Sign Up'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    {...register('name')}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Username"
                    {...register('username')}
                  />
                  {errors.username && (
                    <p className="text-red-500 text-sm">
                      {errors.username.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <Input
                    id="bio"
                    type="text"
                    placeholder="Short bio (optional)"
                    {...register('bio')}
                  />
                  {errors.bio && (
                    <p className="text-red-500 text-sm">{errors.bio.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select
                    onValueChange={value => setValue('role', value)}
                    defaultValue={selectedRole}
                  >
                    <SelectTrigger className="w-full">
                      {selectedRole}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.role && (
                    <p className="text-red-500 text-sm">
                      {errors.role.message}
                    </p>
                  )}
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Email"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-red-500 text-sm">
                  {errors.password.message}
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loginLoading || signUpLoading}
            >
              {isLogin
                ? loginLoading
                  ? 'Logging In...'
                  : 'Login'
                : signUpLoading
                ? 'Signing Up...'
                : 'Sign Up'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="text-center">
          <p className="text-sm">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-blue-500 hover:underline"
            >
              {isLogin ? 'Sign Up' : 'Login'}
            </button>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ChatAuthForm;
